import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/roles";
import {
  LayoutDashboard, Home, Droplets, Wallet, Receipt, Map, FileBarChart,
  Users, ShieldCheck, LogOut, Menu, X, Search, Sun, Moon, Building2, Bell, Building, Landmark, Waves, Megaphone, FileCheck,
} from "lucide-react";
import PunjabEmblem from "@/components/PunjabEmblem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: null },
  { to: "/mis", label: "State MIS", icon: Landmark, roles: ["super_admin", "state_admin", "municipal_commissioner", "auditor"] },
  { to: "/properties", label: "Property Tax", icon: Home, roles: null },
  { to: "/water", label: "Water Tax", icon: Droplets, roles: null },
  { to: "/sewerage", label: "Sewerage Tax", icon: Waves, roles: null },
  { to: "/advertisements", label: "Advertisement Tax", icon: Megaphone, roles: null },
  { to: "/licenses", label: "Trade Licenses", icon: FileCheck, roles: null },
  { to: "/building-plans", label: "Building Plans", icon: Building, roles: null },
  { to: "/payments", label: "Payments", icon: Wallet, roles: null },
  { to: "/receipts", label: "Receipts", icon: Receipt, roles: null },
  { to: "/reconciliation", label: "Reconciliation", icon: Landmark, roles: ["bank_admin", "auditor", "state_admin", "municipal_commissioner", "super_admin"] },
  { to: "/gis", label: "GIS Map", icon: Map, roles: null },
  { to: "/reports", label: "Reports", icon: FileBarChart, roles: null },
  { to: "/admin/users", label: "User Management", icon: Users, roles: ["super_admin", "state_admin", "municipal_commissioner"] },
  { to: "/admin/audit", label: "Audit Logs", icon: ShieldCheck, roles: ["super_admin", "state_admin", "municipal_commissioner", "auditor"] },
];

function useDarkMode() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("pulb_theme", next ? "dark" : "light");
  };
  React.useEffect(() => {
    const saved = localStorage.getItem("pulb_theme");
    if (saved === "dark") document.documentElement.classList.add("dark");
  }, []);
  return [dark, toggle];
}

function SidebarNav({ role, onNavigate }) {
  const items = React.useMemo(
    () => NAV.filter((n) => !n.roles || n.roles.includes(role) || role === "super_admin"),
    [role]
  );
  return (
    <nav className="flex flex-col gap-1 p-3" data-testid="sidebar-nav">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              isActive
                ? "bg-[#0F2942] text-white"
                : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`
          }
        >
          <n.icon className="w-4 h-4" strokeWidth={1.75} />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function OfficerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [dark, toggleDark] = useDarkMode();
  const [sheetOpen, setSheetOpen] = useState(false);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="officer-sidebar">
        <div className="tricolor-bar" />
        <div className="p-4 flex items-center gap-3 border-b border-slate-200 dark:border-slate-800">
          <PunjabEmblem size={40} />
          <div>
            <div className="font-heading font-semibold text-sm text-slate-900 dark:text-slate-100">PULB DRMS</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Officer Console</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SidebarNav role={user?.role} />
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800 p-3 text-xs text-slate-500">
          Municipal Revenue Platform
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30" data-testid="officer-topbar">
          <div className="flex items-center gap-3">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="btn-open-mobile-nav">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <div className="tricolor-bar" />
                <div className="p-4 flex items-center gap-3 border-b border-slate-200 dark:border-slate-800">
                  <PunjabEmblem size={40} />
                  <div className="font-heading font-semibold text-sm">PULB DRMS</div>
                </div>
                <SidebarNav role={user?.role} onNavigate={() => setSheetOpen(false)} />
              </SheetContent>
            </Sheet>
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleDark} data-testid="btn-toggle-theme">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" data-testid="btn-notifications">
              <Bell className="w-4 h-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2 pr-3" data-testid="btn-user-menu">
                  <Avatar className="w-7 h-7">
                    <AvatarFallback className="bg-[#0F2942] text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-medium">{user?.name}</div>
                    <div className="text-[10px] text-slate-500">{ROLE_LABELS[user?.role]}</div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
